'use client';

import { XMarkIcon } from '@heroicons/react/24/solid';
import {
  CartDrawerBar,
  CartDrawerDismiss,
  CartDrawerPanel,
  CartDrawerTitle,
} from '@vs-shop/commerce-ui/cartDrawer';
import { dismissToast, showErrorToast, showWarningToast } from '@vs-shop/ui/toast';
import { Drawer } from '@vs-shop/ui/drawer';
import { Option, Schema } from 'effect';
import { useIntl } from 'react-intl';
import { useEffect, useRef } from 'react';

import CartRejectionAnnouncer from '../CartRejectionAnnouncer';
import CartDrawerContent from './CartDrawerContent';
import CartDrawerItemPlaceholder from './CartDrawerItemPlaceholder';
import CartDrawerSummary from '../../../../server/page/cart/CartDrawerSummary';
import FreeShippingProgressBar from '../../../../server/page/cart/FreeShippingProgressBar';

import {
  pendingQuantityAtom,
  visiblePendingCartLinesAtom,
} from '../../../../../effect/atom/order/cartMutations';
import { cartAtom, cartDomainIsEmptyAtom, knownCartAtom } from '../../../../../effect/bind/order';
import { openCartDrawerAtom, supportBoxInCartDrawerAtom } from '@vs-shop/engine/atom/ui/cartDrawer';
import {
  projectCartDrawerState,
  type CartDrawerPendingLine,
} from '@vs-shop/engine/module/cart/cartDrawerProjection';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtom, useAtomMount, useAtomRefresh, useAtomValue } from '@effect/atom-react';
import type { Cart as CartView } from '@vs-shop/engine/capability/cart/cartSchema';
import { Price, PriceFromApiSchema } from '@vs-shop/schemas/primitive/price';

const encodeApiPrice = Schema.encodeSync(PriceFromApiSchema);

function renderCartSummary(
  cartView: CartView,
  couponFallback: string,
  pendingLines: ReadonlyArray<CartDrawerPendingLine>
) {
  const serverItemsTotal = Option.getOrElse(cartView.itemsTotalWithTaxAfterDiscount, () => 0);
  const projection = projectCartDrawerState(cartView.items, pendingLines, serverItemsTotal);
  const isProvisional = Option.isSome(projection.provisionalItemsTotal);
  const itemsTotal = Option.match(projection.provisionalItemsTotal, {
    onNone: () => serverItemsTotal,
    onSome: (total) => encodeApiPrice(Price(total)),
  });

  return (
    <CartDrawerSummary
      currency={cartView.currency}
      discount={cartView.discount}
      totalWithTax={itemsTotal}
      isProvisional={isProvisional}
      couponName={Option.getOrElse(
        Option.flatMap(cartView.coupon, (coupon) => coupon.name),
        () => couponFallback
      )}
      freeShippingProgress={Option.match(cartView.freeShipping, {
        onNone: () => null,
        onSome: ({ threshold, remaining }) => (
          <FreeShippingProgressBar
            threshold={threshold}
            remaining={remaining}
            currency={cartView.currency}
          />
        ),
      })}
    />
  );
}

/**
 * The drawer shows the last cart that the backend confirmed and pending adds.
 * A failed read keeps confirmed data visible with a retry control.
 * The placeholder appears before the first read. An empty cart shows its empty state.
 */
export default function CartDrawer() {
  const intl = useIntl();
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useAtom(openCartDrawerAtom);
  const cartResult = useAtomValue(cartAtom);
  const cart = useAtomValue(knownCartAtom);
  const isCartEmpty = useAtomValue(cartDomainIsEmptyAtom);
  const pendingQuantity = useAtomValue(pendingQuantityAtom);
  const pendingLines = useAtomValue(visiblePendingCartLinesAtom);
  const refreshCart = useAtomRefresh(cartAtom);
  const announcedUnresolved = useRef(new Set<number>());

  useEffect(() => {
    for (const line of pendingLines) {
      if (Option.isNone(line.unresolvedCause) || announcedUnresolved.current.has(line.id)) continue;
      announcedUnresolved.current.add(line.id);
      showWarningToast(
        intl.formatMessage({
          id: 'cartDrawer.pendingLine.unresolved',
          defaultMessage: 'Změnu košíku se nepodařilo ověřit',
        })
      );
    }
  }, [pendingLines, intl]);

  useAtomMount(supportBoxInCartDrawerAtom);

  const isWaiting =
    AsyncResult.isWaiting(cartResult) ||
    pendingLines.some((line) => Option.isNone(line.unresolvedCause));
  const hasLines = !isCartEmpty || pendingQuantity > 0;
  const readFailed = AsyncResult.isFailure(cartResult) && !cartResult.waiting;
  const isFirstRead = AsyncResult.isInitial(cartResult) && !hasLines;
  useEffect(() => {
    if (!readFailed) return;
    const toastId = showErrorToast(
      <div className="flex flex-col gap-2">
        <span>
          {intl.formatMessage({
            id: 'cart.error',
            defaultMessage: 'Chyba při načítání košíku',
          })}
        </span>
        <button type="button" className="self-start underline" onClick={refreshCart}>
          {intl.formatMessage({
            id: 'cart.error.retry',
            defaultMessage: 'Zkusit znovu',
          })}
        </button>
      </div>,
      { autoClose: false }
    );
    return () => dismissToast(toastId);
  }, [readFailed, refreshCart, intl]);
  const couponFallback = intl.formatMessage({
    id: 'entity.promotionCoupon.label',
    defaultMessage: 'Kupon',
  });
  const summary = Option.match(cart, {
    onNone: () => null,
    onSome: (cartView) => renderCartSummary(cartView, couponFallback, pendingLines),
  });

  return (
    <>
      <CartRejectionAnnouncer />
      <Drawer open={isCartDrawerOpen} onOpenChange={setIsCartDrawerOpen}>
        <CartDrawerPanel>
          <CartDrawerDismiss
            aria-label={intl.formatMessage({
              id: 'cartDrawer.close',
              defaultMessage: 'Zavřít',
            })}
          >
            <XMarkIcon className="w-6 shrink-0 text-foreground" />
          </CartDrawerDismiss>
          <CartDrawerBar>
            <CartDrawerTitle>
              {intl.formatMessage({
                id: 'cartDrawer.title',
                defaultMessage: 'Seznam produktů',
              })}
            </CartDrawerTitle>
          </CartDrawerBar>

          {isFirstRead ? (
            <CartDrawerItemPlaceholder />
          ) : (
            <CartDrawerContent isWaiting={isWaiting} readFailed={readFailed}>
              {summary}
            </CartDrawerContent>
          )}
        </CartDrawerPanel>
      </Drawer>
    </>
  );
}
